import React from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import {
  GppGood,
  GppBad,
  VolumeOff,
  VolumeUp,
  WarningRounded,
  InfoOutlined,
  Lock,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useSubscriptionStatusWithClock as useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { processEmergencySettings } from './utils';
import { DASHBOARD_COLORS, COMMON_STYLES } from './styles';
import DataRow from './shared/DataRow';

const EmergencySettingsSection = ({
  compassSettings,
  groups,
  organization,
}) => {
  const { t } = useTranslation();
  const subscriptionData = useSubscriptionStatus(groups, organization);
  const emergencySettings = processEmergencySettings(compassSettings);

  const renderIcon = (setting, enabledIcon, disabledIcon) => {
    if (!subscriptionData.hasRespondAccess) return null;

    if (setting === '--') {
      return <WarningRounded sx={{ color: COLORS.GREY }} />;
    }
    return setting ? enabledIcon : disabledIcon;
  };

  return (
    <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <Paper
        variant="contained"
        sx={{
          ...COMMON_STYLES.sectionPaper,
          flex: 1,
          minHeight: 0,
          ...(!subscriptionData.hasRespondAccess && {
            opacity: 0.9,
            pointerEvents: 'none',
            userSelect: 'none',
          }),
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="body1"
            fontSize=".95rem"
            sx={{ fontWeight: 'bold', mr: 1 }}
          >
            {t(StringKeys.EMERGENCY_SERVICE_SETTINGS)}
          </Typography>
          <Tooltip
            title={t(
              StringKeys.YOU_CAN_CHANGE_THESE_SETTINGS_IN_COMPASS_PRO_CONFIGURATIONS_PAGE,
            )}
            placement="bottom-start"
            arrow
          >
            <InfoOutlined sx={{ color: DASHBOARD_COLORS.primary }} fontSize="small" />
          </Tooltip>
        </Box>

        {/* Settings */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <DataRow label={t(StringKeys.ARC_FLASH_DETECTION)}>
            {renderIcon(
              emergencySettings.arcFlashDetection,
              <GppGood sx={{ color: DASHBOARD_COLORS.success }} />,
              <GppBad sx={{ color: COLORS.GREY }} />,
            )}
          </DataRow>

          <DataRow label={t(StringKeys.SOS_BUTTON_SOUND)}>
            {renderIcon(
              emergencySettings.muteSosButton,
              <VolumeOff sx={{ color: COLORS.GREY }} />,
              <VolumeUp sx={{ color: DASHBOARD_COLORS.success }} />,
            )}
          </DataRow>

          <DataRow label={t(StringKeys.FALL_DETECTION)}>
            {renderIcon(
              emergencySettings.fallDetection,
              <GppGood sx={{ color: DASHBOARD_COLORS.success }} />,
              <GppBad sx={{ color: COLORS.GREY }} />,
            )}
          </DataRow>

          <DataRow label={t(StringKeys.HEAD_IMPACT_DETECTION)}>
            {renderIcon(
              emergencySettings.headImpact,
              <GppGood sx={{ color: DASHBOARD_COLORS.success }} />,
              <GppBad sx={{ color: COLORS.GREY }} />,
            )}
          </DataRow>
        </Box>
      </Paper>

      {/* Conditional Blur Overlay - Only When No Respond Access */}
      {!subscriptionData.hasRespondAccess && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: DASHBOARD_COLORS.headerBackground,
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            borderRadius: '8px',
            zIndex: 2,
            padding: 2,
          }}
        >
          <Lock sx={{ fontSize: 82, color: COLORS.GREY, mb: 2 }} />
          <Typography
            variant="body2"
            sx={{
              color: DASHBOARD_COLORS.mutedText,
              textAlign: 'center',
              fontWeight: 500,
              maxWidth: '300px',
            }}
          >
            {t(StringKeys.EMERGENCY_SERVICE_SETTINGS_NO_RESPOND_SUBSCRIPTION)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EmergencySettingsSection;
